import React, { useMemo } from "react";

const TnBTable = ({ gtme_performace }) => {
  const api = gtme_performace?.data || {};

  const makeMap = (arr) => {
    const map = {};
    arr?.forEach((item) => {
      map[item.key] = item.percent;
    });
    return map;
  };

  const beatPlanMap = useMemo(() => makeMap(api?.Beat_Plan), [api]);
  const cprMap = useMemo(() => makeMap(api?.CPR), [api]);
  const percentBeatMap = useMemo(() => makeMap(api?.percent_beat_plan), [api]);
  const btlMap = useMemo(() => makeMap(api?.BTL_Adherence), [api]);
  const tqpMap = useMemo(() => makeMap(api?.Tqp_Velocity), [api]);
  const marketMap = useMemo(() => makeMap(api?.Market_Visiblity), [api]);
  const pvinMap = useMemo(() => makeMap(api?.Pvin), [api]);

  /* Generic formatter with target comparison */
  const get = (map, key, target, decimal = 2, suffix = "%") => {
    const val = map?.[key];
    if (val === undefined || val === null) return "-";
    const formatted = suffix
      ? `${val.toFixed(decimal)}${suffix}`
      : val.toFixed(decimal);

    if (!target) {
      return {
        value: formatted,
        color: "text-slate-700",
      };
    }
    const targetNum = Number(target.replace("%", ""));
    return {
      value: formatted,
      color: val >= targetNum ? "text-green-600" : "text-red-500",
    };
  };

  /* CPR formatter (always black except its own rule) */
  const getCPR = (map, key) => {
    const val = map?.[key];
    if (val === undefined || val === null) return "-";
    return {
      value: val.toFixed(0),
      color: "text-black",
    };
  };

  const data = useMemo(() => {
    return [
      {
        param: "Beat plan adherence - Physical - Geo Tagging",
        tgt: "80%",

        hcv: get(beatPlanMap, "HCV Trucks", "80%"),
        ilmcv: get(beatPlanMap, "ILMCV Trucks", "80%"),
        cvp: get(beatPlanMap, "CV Passenger", "80%"),
      },
      {
        param: "CPR",
        tgt: "-",

        hcv: getCPR(cprMap, "HCV Trucks"),
        ilmcv: getCPR(cprMap, "ILMCV Trucks"),
        cvp: getCPR(cprMap, "CV Passenger"),
      },
      {
        param: "% Beat Plan to Retail",
        tgt: "30%",

        hcv: get(percentBeatMap, "HCV Trucks", "30%"),
        ilmcv: get(percentBeatMap, "ILMCV Trucks", "30%"),
        cvp: get(percentBeatMap, "CV Passenger", "30%"),
      },
      {
        param: "BTL Adherence",
        tgt: "80%",

        hcv: get(btlMap, "HCV Trucks", "80%"),
        ilmcv: get(btlMap, "ILMCV Trucks", "80%"),
        cvp: get(btlMap, "CV Passenger", "80%"),
      },
      {
        param: "TQP Velocity (C3/TQP 25th)",
        tgt: "70%",

        hcv: get(tqpMap, "HCV Trucks", "70%"),
        ilmcv: get(tqpMap, "ILMCV Trucks", "70%"),
        cvp: get(tqpMap, "CV Passenger", "70%"),
      },
      {
        param: "Market Visibility",
        tgt: "90%",

        hcv: get(marketMap, "HCV Trucks", "90%"),
        ilmcv: get(marketMap, "ILMCV Trucks", "90%"),
        cvp: get(marketMap, "CV Passenger", "90%"),
      },
      {
        param: "%PVIN Usage",
        tgt: "80%",

        hcv: get(pvinMap, "HCV Trucks", "80%"),
        ilmcv: get(pvinMap, "ILMCV Trucks", "80%"),
        cvp: get(pvinMap, "CV Passenger", "80%"),
      },
    ];
  }, [beatPlanMap, cprMap, percentBeatMap, btlMap, tqpMap, marketMap, pvinMap]);

  const columns = [
    { header: "Parameter", accessor: "param", align: "text-left" },
    { header: "Target", accessor: "tgt", align: "text-center" },
    { header: "HCV", accessor: "hcv", align: "text-center" },
    { header: "ILMCV", accessor: "ilmcv", align: "text-center" },
    { header: "CVP", accessor: "cvp", align: "text-center" },
  ];

  return (
    <div className="p-0 bg-white m-2 overflow-hidden w-[400px] md:w-[1200px] rounded-b-xl border border-slate-200 shadow-inner">
      <div className="overflow-x-auto custom-scrollbar rounded-t-lg max-h-[400px]">
        <table className="w-full min-w-max border-collapse">
          <thead className="sticky top-0 z-20">
            <tr className="bg-[#234b63]">
              {columns.map((col) => (
                <th
                  key={col.accessor}
                  className={`text-white font-bold px-3 py-2 text-sm border-r border-[#315d7a] last:border-0 ${col.align}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.map((row, idx) => (
              <tr
                key={idx}
                className={`border-b border-slate-100 ${
                  idx % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                }`}
              >
                {columns.map((col) => {
                  const val = row[col.accessor];
                  return (
                    <td
                      key={col.accessor}
                      className={`px-4 py-2 text-sm border-r border-slate-100 last:border-0 ${col.align}`}
                    >
                      {col.accessor === "param" ? (
                        <div className="font-semibold text-slate-800">
                          {val}
                        </div>
                      ) : col.accessor === "tgt" ? (
                        <div className="inline-block bg-slate-100 font-bold text-slate-900 rounded px-2.5 py-1 min-w-[50px]">
                          {val}
                        </div>
                      ) : typeof val === "object" ? (
                        <div className={`font-semibold ${val.color}`}>
                          {val.value}
                        </div>
                      ) : (
                        <div className="text-slate-600 font-medium">{val}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default React.memo(TnBTable);
